import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Header from "../components/Header";
import { Plus, ArrowUpRight, Pulse, Database, Target, ClockClockwise, Folders } from "@phosphor-icons/react";

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function timeUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const [pr, st] = await Promise.all([
        api.get("/projects"),
        api.get("/stats/dashboard"),
      ]);
      setProjects(pr.data.projects || []);
      setStats(st.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      <main className="px-6 md:px-10 py-8 max-w-[1500px] mx-auto" data-testid="dashboard-page">
        {/* Hero strip */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] font-mono text-[#ffb000] uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
              <span className="pulse-dot w-1.5 h-1.5 bg-[#ffb000] inline-block" /> OPERATOR CONSOLE
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tighter leading-none">
              Lead<span className="text-[#ffb000]">.</span>Intelligence
            </h1>
            <p className="text-[#888] mt-3 max-w-2xl text-sm">
              All projects, all monitored sources, all qualified leads — in one terminal.
            </p>
          </div>
          <Link
            to="/projects/new"
            data-testid="new-project-btn"
            className="btn-primary flex items-center gap-2 self-start md:self-end"
          >
            <Plus size={14} weight="bold" /> NEW PROJECT
          </Link>
        </div>

        {/* Stats bento */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a] mb-10" data-testid="dashboard-stats">
          {[
            { label: "PROJECTS", value: stats?.projects_count ?? "—", icon: Target, accent: "#ffb000" },
            { label: "TOTAL LEADS", value: stats?.leads_total ?? "—", icon: Database, accent: "#ededed" },
            { label: "NEW / UNREVIEWED", value: stats?.leads_new ?? "—", icon: Pulse, accent: "#00ff66" },
            { label: "RUNS (24H)", value: stats?.runs_24h ?? "—", icon: ClockClockwise, accent: "#00e5ff" },
          ].map((s) => (
            <div key={s.label} className="bg-[#050505] p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="label-tag">{s.label}</span>
                <s.icon size={14} color={s.accent} />
              </div>
              <div className="text-3xl md:text-4xl font-bold font-mono" style={{ color: s.accent }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Section header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold tracking-tight">Projects</h2>
            <span className="label-tag">[{projects.length}]</span>
          </div>
          <div className="text-[10px] font-mono text-[#555] tracking-[0.2em] uppercase">
            sorted: created desc
          </div>
        </div>

        {err && (
          <div className="mb-4 p-3 border border-[#ff3b30]/40 bg-[#ff3b30]/10 text-[#ff3b30] text-xs font-mono">
            {err}
          </div>
        )}

        {loading ? (
          <div className="panel p-10 text-center text-[#888] font-mono text-xs">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="panel p-12 text-center" data-testid="empty-projects">
            <Folders size={48} className="mx-auto text-[#555] mb-4" weight="thin" />
            <div className="text-lg mb-2">No projects yet.</div>
            <p className="text-[#888] text-sm mb-6 font-mono">
              // each project = one product/site you want to find leads for
            </p>
            <Link to="/projects/new" className="btn-primary inline-flex items-center gap-2">
              <Plus size={14} weight="bold" /> CREATE FIRST PROJECT
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                data-testid={`project-card-${p.id}`}
                className="bg-[#050505] p-5 hover:bg-[#0a0a0a] transition-colors group block"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono text-[#555] uppercase tracking-[0.2em] mb-1">
                      PROJ_{p.id.slice(0, 8)}
                    </div>
                    <div className="text-lg font-semibold tracking-tight text-[#ededed] group-hover:text-[#ffb000] transition-colors truncate">
                      {p.name}
                    </div>
                  </div>
                  <ArrowUpRight size={18} className="text-[#555] group-hover:text-[#ffb000] transition-colors" />
                </div>
                <p className="text-xs text-[#888] line-clamp-2 mb-4 min-h-[32px]">
                  {p.problem_criteria || p.product_description || "No criteria set."}
                </p>

                <div className="grid grid-cols-3 gap-px bg-[#1a1a1a] border-t border-b border-[#1a1a1a]">
                  <div className="bg-[#050505] p-2">
                    <div className="text-[9px] font-mono text-[#555] uppercase tracking-widest">LEADS</div>
                    <div className="font-mono text-base text-[#ededed]">{p.stats?.leads_total ?? 0}</div>
                  </div>
                  <div className="bg-[#050505] p-2">
                    <div className="text-[9px] font-mono text-[#555] uppercase tracking-widest">NEW</div>
                    <div className="font-mono text-base text-[#00ff66]">{p.stats?.leads_new ?? 0}</div>
                  </div>
                  <div className="bg-[#050505] p-2">
                    <div className="text-[9px] font-mono text-[#555] uppercase tracking-widest">SOURCES</div>
                    <div className="font-mono text-base text-[#00e5ff]">{p.stats?.sources_active ?? 0}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 text-[10px] font-mono">
                  <span className={`tag ${p.enabled ? "tag-success" : "tag-muted"}`}>
                    <span className={`w-1.5 h-1.5 ${p.enabled ? "bg-[#00ff66] pulse-dot" : "bg-[#555]"}`} />
                    {p.enabled ? "ACTIVE" : "PAUSED"}
                  </span>
                  <span className="text-[#555]">
                    next: <span className="text-[#ededed]">{timeUntil(p.next_run_at) || "—"}</span>
                  </span>
                </div>

                <div className="text-[10px] font-mono text-[#555] mt-3 uppercase tracking-widest">
                  Last run: {fmtTime(p.last_run_at)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
